/**
 * @fileoverview Shared icon/colour/label helpers for rendering tool calls
 * across both the v1 thread message timeline and the v2 activity-log feed.
 * @module shared-panels/tool-display
 *
 * Why it exists:
 *   The same tool name needs identical visual treatment everywhere it is
 *   rendered. Extracting these helpers into one module avoids drift between
 *   `InlineAgentMessage` (v1 thread tool calls) and `TimelineToolCallEntry`
 *   (v2 activity-log tool pills).
 *
 * Naming conventions handled:
 *   - camelCase: `getDocumentMetadata` → "Get Document Metadata"
 *   - snake_case: `submit_final_output` → "Submit Final Output"
 *   - namespaced snake_case: `jobContext_getJobMetadata` → "Get Job Metadata"
 */

import type { BadgeColor } from "@/ui/primitives/badge";
import { rewriteAssumedRelationBackCallout } from "@cvx/keyDates/relationBackCopy";
import {
  CheckCircle2,
  Database,
  FileText,
  ListChecks,
  Merge,
  Play,
  PlusCircle,
  RefreshCw,
  Search,
  Send,
  Wrench,
} from "lucide-react";
import type React from "react";
import { createElement } from "react";

const ICON_CLASS = "h-2.5 w-2.5";

const TOOL_ICONS: Record<string, React.ReactNode> = {
  getJobDetails: createElement(Database, { className: ICON_CLASS }),
  getDocumentMetadata: createElement(FileText, { className: ICON_CLASS }),
  getDocumentContent: createElement(FileText, { className: ICON_CLASS }),
  getDocumentExtractions: createElement(Database, { className: ICON_CLASS }),
  listDocumentsByCategory: createElement(Search, { className: ICON_CLASS }),
  searchDocuments: createElement(Search, { className: ICON_CLASS }),
  createTask: createElement(PlusCircle, { className: ICON_CLASS }),
  dispatchWorkers: createElement(Play, { className: ICON_CLASS }),
  getTaskTree: createElement(ListChecks, { className: ICON_CLASS }),
  completeAnalysis: createElement(CheckCircle2, { className: ICON_CLASS }),
  completeWorkerTask: createElement(CheckCircle2, { className: ICON_CLASS }),
  completeDedup: createElement(CheckCircle2, { className: ICON_CLASS }),
  submit_final_output: createElement(Send, { className: ICON_CLASS }),
};

const TOOL_COLORS: Record<string, BadgeColor> = {
  getDocumentMetadata: "blue",
  getDocumentContent: "blue",
  getDocumentExtractions: "indigo",
  listDocumentsByCategory: "sky",
  searchDocuments: "sky",
  createTask: "amber",
  dispatchWorkers: "emerald",
  getTaskTree: "purple",
  completeAnalysis: "emerald",
  completeWorkerTask: "emerald",
  completeDedup: "emerald",
  submit_final_output: "emerald",
};

/**
 * Strips a leading `namespace_` token (e.g. `jobContext_`) so namespaced tool
 * names render as just the tool action.
 */
function stripNamespace(name: string): string {
  const idx = name.indexOf("_");
  if (idx <= 0) return name;
  // Only strip if both sides look like identifiers (no double-underscores etc.)
  const head = name.slice(0, idx);
  const tail = name.slice(idx + 1);
  if (!head || !tail) return name;
  return tail;
}

export function getToolIcon(name: string): React.ReactNode {
  if (TOOL_ICONS[name]) return TOOL_ICONS[name];

  // Verb-based fallbacks — match by camelCase or snake_case action prefix.
  const action = stripNamespace(name);
  const lower = action.toLowerCase();

  if (lower.startsWith("add") || lower.startsWith("insert"))
    return createElement(PlusCircle, { className: ICON_CLASS });
  if (lower.startsWith("merge") || lower.startsWith("unmerge"))
    return createElement(Merge, { className: ICON_CLASS });
  if (lower.startsWith("update"))
    return createElement(RefreshCw, { className: ICON_CLASS });
  if (lower.startsWith("list") || lower.startsWith("search"))
    return createElement(Search, { className: ICON_CLASS });
  if (lower.startsWith("complete") || lower.startsWith("submit"))
    return createElement(CheckCircle2, { className: ICON_CLASS });
  if (lower.startsWith("get") || lower.startsWith("read"))
    return createElement(Database, { className: ICON_CLASS });

  return createElement(Wrench, { className: ICON_CLASS });
}

export function getToolColor(name: string): BadgeColor {
  if (TOOL_COLORS[name]) return TOOL_COLORS[name];

  const action = stripNamespace(name);
  const lower = action.toLowerCase();

  if (lower.startsWith("add") || lower.startsWith("insert")) return "amber";
  if (lower.startsWith("merge") || lower.startsWith("unmerge")) return "violet";
  if (lower.startsWith("update")) return "orange";
  if (lower.startsWith("list") || lower.startsWith("search")) return "sky";
  if (lower.startsWith("complete") || lower.startsWith("submit"))
    return "emerald";
  if (lower.startsWith("get") || lower.startsWith("read")) return "blue";

  return "gray";
}

/**
 * Parses an activity-log message produced by `ManagedAgentEventBridge`.
 *
 * Messages are emitted as structured strings of the form:
 *   `[<Prefix>] Calling tool <toolName>`
 *   `[<Prefix>] Tool <toolName> returned`
 *   `[<Prefix>] CALLOUT: <body>`
 *   `[<Prefix>] Error: <body>`
 *   `[<Prefix>] Session created (agent v<n>)`
 *
 * `applyOutput` also writes turn-summary lines (`[<Prefix>] <summary> (...counts)`).
 *
 * The agent card needs to render the tool-call cases as colored pills (matching
 * the unified timeline view) instead of dumping the raw string. Other cases
 * fall back to plain text — only the `kind` discriminator and (optionally) a
 * `toolName` are extracted; the rest of the message is returned in `rest`.
 */
export type ParsedActivity =
  | { kind: "tool_call"; toolName: string; prefix?: string }
  | { kind: "tool_result"; toolName: string; prefix?: string }
  | { kind: "callout"; body: string; prefix?: string }
  | { kind: "error"; body: string; prefix?: string }
  | { kind: "info"; body: string; prefix?: string };

const PREFIX_RE = /^\s*\[([^\]]+)\]\s*(.*)$/;

export function parseActivityMessage(msg: string): ParsedActivity {
  const match = PREFIX_RE.exec(msg);
  const prefix = match?.[1];
  const body = match ? match[2] : msg;

  const toolCall = /^Calling tool (\S+)/.exec(body);
  if (toolCall) return { kind: "tool_call", toolName: toolCall[1], prefix };

  const toolResult = /^Tool (\S+) returned/.exec(body);
  if (toolResult)
    return { kind: "tool_result", toolName: toolResult[1], prefix };

  if (body.startsWith("CALLOUT:"))
    return {
      kind: "callout",
      body: rewriteAssumedRelationBackCallout(
        body.slice("CALLOUT:".length).trim(),
      ),
      prefix,
    };

  if (body.startsWith("Error:"))
    return { kind: "error", body: body.slice("Error:".length).trim(), prefix };

  return { kind: "info", body, prefix };
}

/**
 * Formats a tool identifier into a readable label.
 *   `getDocumentExtractions` → "Get Document Extractions"
 *   `submit_final_output`    → "Submit Final Output"
 *   `jobContext_getJobMetadata` → "Get Job Metadata"
 */
export function formatToolName(name: string): string {
  const stripped = stripNamespace(name);
  // Normalise: camelCase → spaces, snake_case → spaces.
  const spaced = stripped
    .replace(/_+/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\s+/g, " ")
    .trim();
  if (!spaced) return name;
  // Title-case each word.
  return spaced
    .split(" ")
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}
